/**
 * Il velo che copre l'app quando torna in primo piano dopo essere stata via.
 *
 * Avvolge l'applicazione e, quando il blocco scatta, mette davanti una schermata piena invece di
 * smontare l'albero: smontarlo perderebbe la pagina aperta, i filtri, il form a metà — e chi ha
 * appena messo via il telefono per un minuto se li ritroverebbe azzerati per una misura di
 * sicurezza che non gli è stata chiesta in quel momento.
 *
 * Sul web e ad app-lock spento è **trasparente**: rende i figli e nient'altro, senza listener e
 * senza import del plugin.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { IconFingerprint, IconLock } from '@tabler/icons-react';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { observeReturns, unlock } from '@/shared/lib/appLock';

export function AppLockGate({ children }: { children: ReactNode }) {
  const [bloccato, setLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => observeReturns(() => setLocked(true)), []);

  const askUnlock = useCallback(() => {
    setInProgress(true);
    setError(null);
    void unlock().then((outcome) => {
      if (outcome.ok) { setLocked(false); return; }
      /* Annullare non è un errore da mostrare in rosso: la schermata resta, e il pulsante pure.
         Chi ha annullato per sbaglio lo ripreme senza sentirsi rimproverare. */
      if (outcome.cause !== 'annullato') setError(outcome.message);
    }).finally(() => setInProgress(false));
  }, []);

  /* Allo scattare del blocco si chiede subito: se l'utente ha appena ripreso in mano il telefono,
     il dialogo di sistema è già quello che si aspetta di vedere. */
  useEffect(() => { if (bloccato) askUnlock(); }, [bloccato, askUnlock]);

  if (!bloccato) return <>{children}</>;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Applicazione bloccata"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--mantine-color-body, #fff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px calc(24px + env(safe-area-inset-right, 0px)) calc(24px + env(safe-area-inset-bottom, 0px)) calc(24px + env(safe-area-inset-left, 0px))',
      }}
    >
      <Stack gap={16} align="center" style={{ maxWidth: 360, width: '100%' }}>
        <IconLock size={40} stroke={1.5} />
        <Text fw={700} size="lg">MaeFwk10 è bloccata</Text>
        <Text size="sm" c="dimmed" ta="center">
          Per riprendere da dove eri, sblocca con l’impronta, il volto o il codice del dispositivo.
          Quello che stavi facendo è ancora qui.
        </Text>

        <Button
          fullWidth
          leftSection={<IconFingerprint size={18} />}
          loading={inProgress}
          onClick={askUnlock}
        >
          Sblocca
        </Button>

        {error && (
          <MaeAdvice
            tone="warning"
            title="Sblocco non riuscito"
            selfRemedy="Riprova, oppure usa il codice del dispositivo dalla stessa finestra di sistema."
          >
            {error}
          </MaeAdvice>
        )}
      </Stack>
    </div>
  );
}
